"use client";

import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { RefreshCw, Play, Clock, CheckCircle, XCircle, AlertCircle, ArrowLeft } from "lucide-react";
import PermissionGuard from "@/components/PermissionGuard";
import { PermissionSubject } from "@/utils/permissions";
import { useOPhimSyncStore } from "@/stores/ophimSyncStore";
import { SyncStatusEnum, SyncTypeEnum, SyncLog } from "@/types/kkmovie";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { toast } from "sonner";

const statusConfig: Record<SyncStatusEnum, { label: string; icon: any; class: string }> = {
  [SyncStatusEnum.PENDING]: { label: "Chờ xử lý", icon: Clock, class: "bg-gray-100 text-gray-800" },
  [SyncStatusEnum.IN_PROGRESS]: { label: "Đang chạy", icon: RefreshCw, class: "bg-blue-100 text-blue-800" },
  [SyncStatusEnum.SUCCESS]: { label: "Thành công", icon: CheckCircle, class: "bg-green-100 text-green-800" },
  [SyncStatusEnum.FAILED]: { label: "Thất bại", icon: XCircle, class: "bg-red-100 text-red-800" },
  [SyncStatusEnum.PARTIAL]: { label: "Một phần", icon: AlertCircle, class: "bg-yellow-100 text-yellow-800" },
};

const typeLabels: Record<SyncTypeEnum, string> = {
  [SyncTypeEnum.MANUAL]: "Thủ công",
  [SyncTypeEnum.SCHEDULED]: "Tự động",
  [SyncTypeEnum.SINGLE]: "Phim đơn",
  [SyncTypeEnum.IMPORT_ALL]: "Import tất cả",
  [SyncTypeEnum.DAILY_NEW_IMPORT]: "Import mới hàng ngày",
};

function formatDate(dateString: string) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('vi-VN');
}

function formatDuration(startedAt: string, completedAt?: string) {
  if (!startedAt) return '-';
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const duration = Math.floor((end - start) / 1000);

  if (duration < 60) return `${duration}s`;
  if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
  return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
}

export default function OPhimSyncPage() {
  const navigate = useNavigate();
  const {
    syncStatus,
    syncHistory,
    historyPagination,
    isLoading,
    isSyncing,
    error,
    fetchSyncStatus,
    fetchSyncHistory,
    triggerSync,
  } = useOPhimSyncStore();

  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchSyncStatus();
    fetchSyncHistory();
  }, [fetchSyncStatus, fetchSyncHistory]);

  // Auto refresh when sync is running
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh || syncStatus?.isRunning) {
      interval = setInterval(() => {
        fetchSyncStatus();
        fetchSyncHistory();
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, syncStatus?.isRunning, fetchSyncStatus, fetchSyncHistory]);

  const handleTriggerSync = async () => {
    const success = await triggerSync();
    if (success) {
      toast.success("Đã bắt đầu sync tất cả phim OPhim");
    } else {
      toast.error(error || "Không thể bắt đầu sync");
    }
  };

  const handleRefresh = () => {
    fetchSyncStatus();
    fetchSyncHistory();
  };

  const handleHistoryPage = (page: number) => {
    fetchSyncHistory(page, historyPagination.limit);
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.KKMOVIES)}
              className="hover:cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Quay lại
            </Button>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-600 to-red-600">
                OPhim Sync
              </h1>
              <p className="mt-1 text-gray-500">Quản lý đồng bộ dữ liệu từ OPhim API</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex items-center gap-2 hover:cursor-pointer"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              Làm mới
            </Button>
            <PermissionGuard subject={PermissionSubject.OPHIM_SYNC} action="update">
              <Button
                onClick={handleTriggerSync}
                disabled={isSyncing || syncStatus?.isRunning}
                className="bg-purple-600 text-white hover:bg-purple-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer"
              >
                <Play className={`h-4 w-4 ${isSyncing ? "animate-pulse" : ""}`} />
                {isSyncing || syncStatus?.isRunning ? "Đang sync..." : "Bắt đầu Sync"}
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Current Status */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Trạng thái hiện tại</h3>
            {syncStatus?.isRunning ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-purple-600 animate-spin" />
                  <span className="text-lg font-semibold text-purple-600">Đang chạy sync</span>
                </div>
                {syncStatus.currentSync && (
                  <>
                    <div className="text-sm text-gray-600">
                      Tiến độ: {syncStatus.currentSync.processedMovies}/{syncStatus.currentSync.totalMovies}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${syncStatus.currentSync.totalMovies > 0
                            ? (syncStatus.currentSync.processedMovies / syncStatus.currentSync.totalMovies) * 100
                            : 0}%`
                        }}
                      />
                    </div>
                    <div className="text-sm text-gray-500">
                      Tập mới: {syncStatus.currentSync.newEpisodesCount}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-lg font-semibold text-green-600">Sẵn sàng</span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Thống kê</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Phim cần sync:</span>
                <span className="font-semibold">{syncStatus?.stats?.totalMoviesToSync || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Đang chiếu:</span>
                <span className="font-semibold text-purple-600">{syncStatus?.stats?.totalOngoing || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sắp chiếu:</span>
                <span className="font-semibold text-yellow-600">{syncStatus?.stats?.totalUpcoming || 0}</span>
              </div>
            </div>
          </div>

          {/* Last Sync */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Sync gần nhất</h3>
            {syncStatus?.latestSync ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {statusConfig[syncStatus.latestSync.status]?.icon && (
                    <span className={statusConfig[syncStatus.latestSync.status]?.class + ' p-1 rounded'}>
                      {(() => {
                        const Icon = statusConfig[syncStatus.latestSync.status]?.icon;
                        return Icon ? <Icon className="h-4 w-4" /> : null;
                      })()}
                    </span>
                  )}
                  <span className="font-semibold">{statusConfig[syncStatus.latestSync.status]?.label}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {formatDate(syncStatus.latestSync.completedAt || syncStatus.latestSync.startedAt)}
                </div>
                <div className="text-sm text-gray-500">
                  {syncStatus.latestSync.newEpisodesCount} tập mới
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Chưa có lịch sử sync</p>
            )}
          </div>
        </div>

        {/* Auto Refresh Toggle */}
        <div className="mb-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="autoRefreshOphim"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <label htmlFor="autoRefreshOphim" className="text-sm text-gray-600">
            Tự động làm mới mỗi 5 giây
          </label>
        </div>

        {/* Sync History */}
        <div className="shadow-lg rounded-xl bg-white border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Lịch sử Sync</h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Thời gian</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Loại</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Trạng thái</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tiến độ</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tập mới</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Thời lượng</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {syncHistory.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <Clock className="h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-gray-600 font-medium">Chưa có lịch sử sync</p>
                            <p className="text-gray-400 text-sm mt-1">Bấm "Bắt đầu Sync" để đồng bộ dữ liệu</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      syncHistory.map((log: SyncLog) => {
                        const StatusIcon = statusConfig[log.status]?.icon;
                        return (
                          <tr key={log._id} className="transition-all hover:bg-gray-50/60">
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {formatDate(log.startedAt)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {typeLabels[log.type] || log.type}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusConfig[log.status]?.class || 'bg-gray-100 text-gray-800'}`}>
                                {StatusIcon && <StatusIcon className={`h-3 w-3 ${log.status === SyncStatusEnum.IN_PROGRESS ? 'animate-spin' : ''}`} />}
                                {statusConfig[log.status]?.label || log.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {log.processedMovies}/{log.totalMovies}
                              {log.failedCount > 0 && (
                                <span className="ml-2 text-red-600">({log.failedCount} lỗi)</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-green-600">
                              +{log.newEpisodesCount}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatDuration(log.startedAt, log.completedAt)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {historyPagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
                    <div>
                      <p className="text-sm text-gray-700">
                        Trang <span className="font-medium">{historyPagination.page}</span> / {historyPagination.totalPages}
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button
                          onClick={() => handleHistoryPage(Math.max(1, historyPagination.page - 1))}
                          disabled={historyPagination.page === 1}
                          variant="outline"
                          size="sm"
                          className="rounded-l-md hover:cursor-pointer"
                        >
                          Trước
                        </Button>
                        <Button
                          onClick={() => handleHistoryPage(Math.min(historyPagination.totalPages, historyPagination.page + 1))}
                          disabled={historyPagination.page === historyPagination.totalPages}
                          variant="outline"
                          size="sm"
                          className="rounded-r-md hover:cursor-pointer"
                        >
                          Sau
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
